import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { useSimpleEventSubscription } from '@passionware/simple-event';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  type AnySpec,
  type KindMap,
  type KindOf,
  readField,
  type SpecOf,
  type UnitValues
} from 'v5/core/form-schema/formKinds.types';
import {
  RowList,
  RowListCopy,
  RowListDisplayRow,
  RowListInput,
  RowListLeadSign,
  RowListPart,
  RowListRemove,
  RowListRow
} from 'v5/patterns/forms/row-list/RowList';
import { RowListSortableRow } from 'v5/patterns/forms/row-list/RowListSortableRow';
import { SortableRows } from 'v5/patterns/forms/row-list/SortableRows';
import { useRowListFieldArray } from 'v5/patterns/forms/row-list/useRowListFieldArray';
import {
  PLACEHOLDER_ROW_KEY,
  VALUE_PART
} from 'v5/patterns/forms/row-list/useRowListKeyboard';
import { cn } from 'v5/platform/dom/cn';
import { FieldContextProvider, useFieldContext } from './FieldContext';
import type { ListItemsFieldProps } from './ListItemsField';
import {
  type ListFormRow,
  newListFormRow,
  readRowValues
} from './listFormRows';
import { RenderField } from './RenderField';
import type { FieldKit, FormKit } from './schemaForm.types';
import { fieldPath, UNIT_ADD_ATTRIBUTE } from './unitPath';
import { usePlaceholderTyping } from './usePlaceholderTyping';

/** The kit's one-line contract for the kind of `spec`. */
export function kindListRow<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>
): FieldKit<M, K>['listRow'] {
  return kit[spec.kind].listRow;
}

/** The kit's heading for the row of a list whose rows hold only `spec` (a rate's name and amount). */
export function kindHeading<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>,
  path: string
): ReactNode | null {
  const Heading = kit[spec.kind].RowHeading;
  return Heading === undefined ? null : <Heading spec={spec} path={path} />;
}

/** The text a line at rest offers to copy, from the kit of the kind of `spec`. */
function kindCopyText<M extends KindMap<M>, K extends KindOf<M>>(
  kit: FormKit<M>,
  spec: SpecOf<M, K>,
  values: UnitValues<M>
): string | null {
  const copyText = kit[spec.kind].listRow?.copyText;
  return copyText === undefined ? null : copyText(readField(values, spec));
}

/** The kind's icon at the start of a line, if it has one. */
function KindLead<M extends KindMap<M>>({
  kit,
  spec
}: {
  kit: FormKit<M>;
  spec: AnySpec<M>;
}) {
  const Icon = kindListRow(kit, spec)?.LeadIcon;
  return Icon === undefined ? null : <RowListLeadSign icon={Icon} />;
}

/** Lists whose rows hold one field of a kind that fits a line. */
export function hasListLines<M extends KindMap<M>>(
  kit: FormKit<M>,
  fields: ReadonlyArray<AnySpec<M>>
): boolean {
  return fields.length === 1 && kindListRow(kit, fields[0]) !== undefined;
}

/**
 * The rows of a list unit whose rows hold one field (an array field group
 * with one member): a line per row, the field's own control without its
 * label, like locations. Edited, the lines share the row-list keyboard and
 * an empty line at the end adds a row: typed into, or pressed when the kind
 * can't start from text (`FieldKit.listRow`).
 */
export function ListLinesField<M extends KindMap<M>>(
  props: ListItemsFieldProps<M>
) {
  const ctx = useFieldContext();
  return ctx.mode === 'edit' ? (
    <LinesEditor {...props} />
  ) : (
    <LinesDisplay {...props} />
  );
}

function LinesDisplay<M extends KindMap<M>>({
  unit,
  name,
  kit
}: ListItemsFieldProps<M>) {
  const ctx = useFieldContext();
  const rows =
    useWatch<Record<string, ListFormRow<M>[] | undefined>>({ name }) ?? [];
  const field = unit.item.fields[0];
  // An empty list shows only its unit's title and "no value".
  if (rows.length === 0 || field === undefined) return null;
  return (
    <RowList role="list" tone="plain" size={ctx.size} aria-label={unit.title}>
      {rows.map((row, index) => (
        <RowListDisplayRow key={row.rowKey} role="listitem">
          <KindLead kit={kit} spec={field} />
          <RowListPart column="value">
            <div className="min-w-0">
              {/* Print-like at rest: the value without its control's line. */}
              <FieldContextProvider value={{ ...ctx, variant: 'plain' }}>
                <RenderField
                  kit={kit}
                  spec={field}
                  path={fieldPath(`${name}.${index}.values`, field.key)}
                  hideLabel
                />
              </FieldContextProvider>
            </div>
          </RowListPart>
          <LineCopy
            text={kindCopyText(kit, field, readRowValues([field], row.values))}
          />
        </RowListDisplayRow>
      ))}
    </RowList>
  );
}

function LinesEditor<M extends KindMap<M>>({
  unit,
  name,
  kit,
  newItem,
  savedRowKeys,
  addSignal
}: ListItemsFieldProps<M>) {
  const { t } = useTranslation('patterns');
  const ctx = useFieldContext();
  const form = useFormContext();
  const field = unit.item.fields[0];
  const line = field === undefined ? undefined : kindListRow(kit, field);
  const valuePath = (index: number) =>
    fieldPath(`${name}.${index}.values`, field?.key ?? '');

  const list = useRowListFieldArray<ListFormRow<M>>({
    name,
    rowKeyOf: row => row.rowKey,
    reorderable: unit.reorderable,
    rowFacts(_row, index) {
      const path = valuePath(index);
      return {
        blank: line?.isEmpty(form.getValues(path)) ?? false,
        error:
          form.getFieldState(path).error === undefined
            ? null
            : { part: VALUE_PART }
      };
    },
    onShowError: index => void form.trigger(valuePath(index))
  });
  const { fields, rows, remove, move } = list;

  function addRow(values: UnitValues<M>) {
    list.add(newListFormRow(values));
  }

  useSimpleEventSubscription(addSignal?.addListener, optionId =>
    addRow(newItem(optionId))
  );

  const fromTyped = line?.fromTyped;
  const placeholderTyping = usePlaceholderTyping(
    text => fromTyped?.(text) ?? null,
    typed => {
      if (field !== undefined) addRow({ ...newItem(null), [field.key]: typed });
    },
    // The row just started is the last one.
    typed =>
      form.setValue(valuePath(fields.length - 1), typed, { shouldDirty: true })
  );

  if (field === undefined || line === undefined) return null;

  const lineParts = (index: number) => (
    <>
      <KindLead kit={kit} spec={field} />
      <RowListPart column="value" part={VALUE_PART}>
        <div className="flex min-w-0 flex-col justify-center self-stretch">
          {/* The field's own control, chrome-less: the row draws the line. */}
          <FieldContextProvider value={{ ...ctx, variant: 'plain' }}>
            <RenderField
              kit={kit}
              spec={field}
              path={valuePath(index)}
              hideLabel
            />
          </FieldContextProvider>
        </div>
      </RowListPart>
    </>
  );

  return (
    <RowList
      {...list.listProps}
      role="list"
      tone={ctx.variant}
      size={ctx.size}
      aria-label={unit.title}
      className="pt-1"
    >
      {unit.reorderable ? (
        <SortableRows
          ids={fields.map(item => item.id)}
          itemTitle={unit.item.title}
          onMove={move}
        >
          {fields.map((item, index) => {
            const rowKey = rows[index]?.rowKey ?? item.id;
            return (
              <RowListSortableRow
                key={item.id}
                id={item.id}
                rowKey={rowKey}
                label={`${unit.item.title} ${index + 1}`}
                sortable={fields.length > 1}
                fresh={savedRowKeys !== undefined && !savedRowKeys.has(rowKey)}
                onRemove={() => remove(index)}
              >
                {lineParts(index)}
              </RowListSortableRow>
            );
          })}
        </SortableRows>
      ) : (
        // No order to keep: no handles, the values stay where they read at rest.
        fields.map((item, index) => {
          const rowKey = rows[index]?.rowKey ?? item.id;
          const label = `${unit.item.title} ${index + 1}`;
          return (
            <RowListRow
              key={item.id}
              rowKey={rowKey}
              role="listitem"
              aria-label={label}
              fresh={savedRowKeys !== undefined && !savedRowKeys.has(rowKey)}
            >
              {lineParts(index)}
              <RowListRemove
                type="button"
                aria-label={t('RowList.remove', { label })}
                onClick={() => remove(index)}
              />
            </RowListRow>
          );
        })
      )}
      {/* Kept mounted as rows are added: its input keeps the focus (and the keys)
          until the new row's input takes it; its text is controlled, so it starts blank. */}
      <RowListRow rowKey={PLACEHOLDER_ROW_KEY}>
        <KindLead kit={kit} spec={field} />
        {fromTyped === undefined ? (
          <RowListPart column="value" part={VALUE_PART}>
            <button
              type="button"
              // Padded like the row's text input, so the label starts where values do.
              data-element="input-group-input"
              className={cn(
                'flex items-center self-stretch text-left text-fg-muted outline-none hocus:text-fg-default',
                ctx.variant !== 'regular' && 'pl-0'
              )}
              onClick={() => addRow(newItem(null))}
            >
              {unit.add.label}
            </button>
          </RowListPart>
        ) : (
          <RowListInput
            {...placeholderTyping}
            placeholder={unit.add.label}
            aria-label={unit.add.label}
            // Where the unit's “+” sends focus.
            {...{ [UNIT_ADD_ATTRIBUTE]: '' }}
          />
        )}
      </RowListRow>
    </RowList>
  );
}

/** Copies a line's text at rest, when its kind offers one. */
function LineCopy({ text }: { text: string | null }) {
  const { t } = useTranslation('patterns');
  return text === null ? null : (
    <RowListCopy
      value={text}
      aria-label={t('SchemaForm.list.copy', { text })}
    />
  );
}
